<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSidebar } from '@composables/useSidebar';

const props = defineProps<{
  to: string;
  label: string;
  exact?: boolean;
  badge?: string | number;
}>();

const route = useRoute();
const { isCollapsed } = useSidebar();

const isActive = computed(() => {
  if (props.exact) {
    return route.path === props.to;
  }

  return route.path.startsWith(props.to);
});
</script>

<template>
  <Tooltip>
    <TooltipTrigger as-child>
      <RouterLink
        :to="to"
        class="relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150"
        :class="[
          isActive
            ? 'bg-primary/10 text-primary font-semibold'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground font-medium',
          isCollapsed ? 'justify-center' : '',
        ]"
      >
        <span class="relative h-5 w-5 flex-shrink-0">
          <slot name="icon" />
          <span
            v-if="badge != null && isCollapsed"
            class="bg-primary absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
          />
        </span>

        <span v-if="!isCollapsed" class="truncate">
          {{ label }}
        </span>

        <span
          v-if="badge != null && !isCollapsed"
          class="bg-primary/10 text-primary ml-auto flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold"
        >
          {{ badge }}
        </span>

        <span
          v-if="isActive"
          class="bg-primary absolute top-1/2 left-0 h-6 w-[3px] -translate-y-1/2 rounded-r-full"
        />
      </RouterLink>
    </TooltipTrigger>

    <TooltipContent v-if="isCollapsed" side="right" :side-offset="8">
      {{ label }}
    </TooltipContent>
  </Tooltip>
</template>
